import { z } from 'zod';

export const VEHICLE_REDUCER_KEY = 'vehicle';
export type VEHICLE_REDUCER_KEY = typeof VEHICLE_REDUCER_KEY;

/* --- UPDATE EXISTING VEHICLE ACCOUNT FIELDS (read-only step) --- */
export const UpdateVehicleAccountFieldsSubmittingObjectSchema = z.object({
  name: z.string(),
  businessId: z.string(),
  linkTo: z.string(),
  scope: z.string(),
  autoUpdateMiles: z.boolean().nullable(),
  useCalculatedMilesPerMonth: z.boolean().nullable(),
  customMilesPerMonth: z.number().nullable(),
});
export type UpdateVehicleAccountFieldsSubmittingObject = z.infer<
  typeof UpdateVehicleAccountFieldsSubmittingObjectSchema
>;

/* --- MANUAL VEHICLE PAYLOAD --- */
export const ManualVehicleSubmittingObjectSchema = z.object({
  name: z.string(),
  estimate: z.number(),
  scope: z.string(),
  businessId: z.string(),
  linkTo: z.string(),
});
export type ManualVehicleSubmittingObject = z.infer<typeof ManualVehicleSubmittingObjectSchema>;

/* --- KBB VEHICLE PAYLOAD --- */
export const KbbVehicleSubmittingObjectSchema = z.object({
  year: z.string(),
  makeId: z.number(),
  make: z.string(),
  modelId: z.number(),
  model: z.string(),
  trimId: z.number(),
  trim: z.string(),
  condition: z.string(),
  mileage: z.string(),
  zipCode: z.string(),
  vehicleId: z.number(),
  scope: z.string(),
  linkTo: z.string(),
  name: z.string(),
  businessId: z.string(),
  autoUpdateMiles: z.boolean().nullable(),
  useCalculatedMilesPerMonth: z.boolean().nullable(),
  customMilesPerMonth: z.number().nullable(),
});
export type KbbVehicleSubmittingObject = z.infer<typeof KbbVehicleSubmittingObjectSchema>;

/* ---- KBB MAKE ---- */
export const KbbVehicleMakeSchema = z.object({
  makeId: z.number(),
  makeName: z.string(),
});
export type KbbVehicleMake = z.infer<typeof KbbVehicleMakeSchema>;

/* ---- KBB MODEL ---- */
export const KbbVehicleModelSchema = z.object({
  modelId: z.number(),
  modelName: z.string(),
  modelMarketName: z.string(),
  makeId: z.number(),
  makeName: z.string(),
});
export type KbbVehicleModel = z.infer<typeof KbbVehicleModelSchema>;

/* ---- KBB YEAR-MODEL ---- */
export const KbbVehicleYearModelIdSchema = z.object({
  modelYearId: z.number(),
  yearId: z.number(),
  makeId: z.number(),
  modelId: z.number(),
  makeName: z.string(),
  modelName: z.string(),
});
export type KbbVehicleYearModel = z.infer<typeof KbbVehicleYearModelIdSchema>;

/* ---- KBB TRIM ---- */
export const KbbVehicleTrimSchema = z.object({
  trimId: z.number(),
  trimName: z.string(),
  vehicleClass: z.string(),
  modelYearId: z.number(),
  yearId: z.number(),
  makeId: z.number(),
  makeName: z.string(),
  modelId: z.number(),
  modelName: z.string(),
  modelPlusTrimName: z.string(),
  sortOrder: z.number(),
});
export type KbbVehicleTrim = z.infer<typeof KbbVehicleTrimSchema>;

export type KbbVehicleTrimResult = {
  modelYearId: number;
  trims: KbbVehicleTrim[];
};

/* ---- KBB FULL VEHICLE ---- */
export const KbbFullVehicleSchema = z.object({
  vehicleId: z.number(),
  vehicleClass: z.string(),
  vehicleName: z.string(),
  yearId: z.number(),
  makeId: z.number(),
  makeName: z.string(),
  modelId: z.number(),
  modelName: z.string(),
  modelMarketName: z.string(),
  modelYearId: z.number(),
  trimId: z.number(),
  trimName: z.string(),
  modelPlusTrimName: z.string(),
  doors: z.number(),
  bodyStyle: z.string(),
  genericBodyStyle: z.string(),
  oemBodyStyle: z.string(),
  sortOrder: z.number(),
  isConsumer: z.boolean(),
  consumer: z.boolean(),
});
export type KbbFullVehicle = z.infer<typeof KbbFullVehicleSchema>;

/* ---- KBB VALUATION ---- */
export const KbbValuationSchema = z.object({
  priceTypeId: z.number(),
  configuredValue: z.number(),
  priceTypeDisplay: z.string(),
  mileageAdjustment: z.number(),
  optionAdjustment: z.number(),
});
export type KbbValuation = z.infer<typeof KbbValuationSchema>;

export const VehicleAutoUpdateMilesSchema = z.object({
  autoUpdateMiles: z.boolean(),
  useCalculatedMilesPerMonth: z.boolean(),
  customMilesPerMonth: z.number().nullable(),
});
export type VehicleAutoUpdateMiles = z.infer<typeof VehicleAutoUpdateMilesSchema>;
/* ---- REDUX STATE ---- */
export type VehicleStoreState = {
  makes: KbbVehicleMake[];
  models: KbbVehicleModel[];
  trims: KbbVehicleTrim[];
  preview: VehiclePreview | null;
  currentRequestId: { makes?: string; models?: string; trims?: string; preview?: string };
  loading: {
    makes: boolean;
    models: boolean;
    trims: boolean;
    preview: boolean;
  };
  error: {
    makes: string | null;
    models: string | null;
    trims: string | null;
    preview: string | null;
  };
  autoUpdateMiles: VehicleAutoUpdateMiles | null;
};

/* ---- FORM STATE ---- */
export const VehicleDialogStepSchema = z.enum([
  'manual',
  'details',
  'readOnly',
  'condition',
  'fetch',
  'review',
  'confirm',
  'completed',
  'error',
]);
export type VehicleDialogStep = z.infer<typeof VehicleDialogStepSchema>;

export const UserPathSchema = z.enum(['kbb', 'manual']);
export type UserPath = z.infer<typeof UserPathSchema>;

export const VehicleConditionSchema = z.enum(['FAIR', 'GOOD', 'VERY_GOOD', 'EXCELLENT']);
export type VehicleCondition = z.infer<typeof VehicleConditionSchema>;

export enum VehicleConditionLabel {
  FAIR = 'Fair',
  GOOD = 'Good',
  VERY_GOOD = 'Very Good',
  EXCELLENT = 'Excellent',
}

export const VehicleInputModeSchema = z.enum(['makeModel', 'vin']);
export type VehicleInputMode = z.infer<typeof VehicleInputModeSchema>;

export const VehiclePriceTypeSchema = z.enum(['TRADE_IN_VALUE', 'FAIR_PURCHASE_PRICE', 'UNKNOWN']);
export type VehiclePriceType = z.infer<typeof VehiclePriceTypeSchema>;

export enum VehiclePriceTypeLabel {
  TRADE_IN_VALUE = 'Trade-in Value',
  FAIR_PURCHASE_PRICE = 'Fair Purchase Price',
  UNKNOWN = 'Estimated Value',
}

export const VehiclePreviewSchema = z.object({
  year: z.string(),
  makeName: z.string(),
  modelName: z.string(),
  trimName: z.string(),
  zipCode: z.string(),
  miles: z.string(),
  valuation: z.number(),
  priceType: VehiclePriceTypeSchema,
  condition: VehicleConditionSchema,
  vehicleId: z.number(),
});
export type VehiclePreview = z.infer<typeof VehiclePreviewSchema>;

export const MakeDTOSchema = z.object({
  makeId: z.number(),
  makeName: z.string(),
});
export type MakeDTO = z.infer<typeof MakeDTOSchema>;

export const ModelDTOSchema = z.object({
  modelId: z.number(),
  modelName: z.string(),
});
export type ModelDTO = z.infer<typeof ModelDTOSchema>;

export const TrimDTOSchema = z.object({
  trimId: z.number(),
  trimName: z.string(),
  modelYearId: z.number(),
});
export type TrimDTO = z.infer<typeof TrimDTOSchema>;

export const VehicleFormStateSchema = z.object({
  vin: z.string(),
  year: z.string(),
  make: MakeDTOSchema.nullable(),
  model: ModelDTOSchema.nullable(),
  trim: TrimDTOSchema.nullable(),
  miles: z.string(),
  zipCode: z.string(),
  condition: VehicleConditionSchema.nullable(),
  inputMode: VehicleInputModeSchema,
  dialogStep: VehicleDialogStepSchema,
  name: z.string(),
  estimate: z.number(),
  userPath: UserPathSchema,
  vehicleId: z.number().nullable(),
  linkTo: z.string(),
  businessId: z.string(),
  autoUpdateMiles: VehicleAutoUpdateMilesSchema.nullable(),
});
export type VehicleFormState = z.infer<typeof VehicleFormStateSchema>;

declare module 'app-components/utils/redux-store/rootState' {
  export default interface RootState {
    [VEHICLE_REDUCER_KEY]: VehicleStoreState;
  }
}
